/**
 * ConfigProvenance.tsx — read-only history of the scoring config (T-368).
 *
 * Answers "who changed the model, when, on whose approval, and what is live right now" without
 * anyone having to open a database. A governed config whose history you cannot read is only
 * half governed.
 */
import { useEffect, useState } from 'react'
import { fetchVersions, type ConfigVersion } from '@/lib/adminApi'

export function ConfigProvenance() {
  const [versions, setVersions] = useState<ConfigVersion[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    fetchVersions('site')
      .then((v) => !cancelled && setVersions(v))
      .catch((e: unknown) => !cancelled && setError((e as Error).message))
    return () => {
      cancelled = true
    }
  }, [])

  if (error)
    return (
      <p role="alert" className="text-sm text-destructive">
        {error}
      </p>
    )
  if (!versions) return <p className="text-sm text-muted-foreground">Loading history…</p>
  if (versions.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No configuration versions yet — scoring is running on the shipped defaults.
      </p>
    )
  }

  return (
    <table className="w-full text-left text-sm text-foreground">
      <caption className="sr-only">Scoring configuration versions</caption>
      <thead className="text-xs text-muted-foreground">
        <tr>
          <th scope="col" className="py-1">
            Version
          </th>
          <th scope="col">Status</th>
          <th scope="col">Change</th>
          <th scope="col">Approved by</th>
          <th scope="col">Created by</th>
        </tr>
      </thead>
      <tbody>
        {versions.map((v) => (
          <tr key={v.id} className="border-t border-border">
            <td className="py-1">
              v{v.version}
              {v.active ? <span className="ml-1 text-xs font-medium">(live)</span> : null}
            </td>
            <td>{v.status}</td>
            <td>{v.note ?? '—'}</td>
            <td>{v.approvedBy ? `${v.approvedBy} · ${v.approvalRef ?? ''}` : '—'}</td>
            <td>{v.createdBy}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
